import React, { useState, useEffect } from 'react'
import { useAuth } from '../context/AuthContext.js'
import './TenantFeatures.css'

const DEFAULT_FEATURES = {
  allow_custom_rules: false,
  allow_quiz: true,
  allow_language_management: true,
  auto_checkout_at_midnight: false,
  delivery_qr_generation: false,
  info_pages_enabled: true,
  welcome_screen_enabled: true,
  sip_enabled: false,
  standby_settings_enabled: false,
  theme_settings_enabled: true,
  sms_enabled: false,
  email_enabled: true,
  lpr_enabled: true,
  safety_rules_enabled: true,
  documents_enabled: true,
  daily_report_enabled: true,
  translations_enabled: true,
  license_plates_enabled: true,
  allowlist_enabled: true,
  users_enabled: false,
  destinations_enabled: false,
  destination_slot_minutes: 60,
  destination_view: 'board',
  destination_free_abandoned: false,
  destination_show_unfinished: true,
  kiosk_checkin_policy: null,
  barrier_control_enabled: false,
  kiosk_display_control_enabled: false,
  deliveries_enabled: false,
}

const KIOSK_CHECKIN_POLICY_OPTIONS = [
  { value: '',                   label: 'Immer blockieren (Standard)' },
  { value: 'open_session_only',  label: 'Nur wenn noch eingecheckt' },
  { value: 'unrestricted',       label: 'Uneingeschränkt' },
]

const FEATURE_LABELS = [
  {
    key: 'allow_custom_rules',
    label: 'Eigene Regeltext-Eingabe',
    description: 'Erlaubt dem Mandant-Admin, eigene Regeltexte einzugeben (zusätzlich zur Bibliotheksauswahl)',
  },
  {
    key: 'allow_quiz',
    label: 'Quiz aktiviert',
    description: 'Wenn deaktiviert, wird das Quiz für den Mandant-Admin ausgeblendet',
  },
  {
    key: 'allow_language_management',
    label: 'Sprachverwaltung aktiviert',
    description: 'Wenn deaktiviert, kann der Mandant-Admin die aktiven Sprachen nicht verwalten',
  },
  {
    key: 'auto_checkout_at_midnight',
    label: 'Automatischer Checkout um Mitternacht',
    description: 'Besucher, die sich bis Mitternacht nicht ausgecheckt haben, werden automatisch als "System-Checkout" abgemeldet.',
  },
  {
    key: 'delivery_qr_generation',
    label: 'QR-Code für Lieferanten generieren',
    description: 'Ermöglicht Admins, im Dashboard nachträglich einen QR-Code für Lieferanten-Check-ins zu erstellen.',
  },
  {
    key: 'info_pages_enabled',
    label: 'Informationsseiten',
    description: 'Zeigt den Menüpunkt "Informationsseiten" im Dashboard des Mandanten an.',
  },
  {
    key: 'welcome_screen_enabled',
    label: 'Willkommensbildschirm',
    description: 'Zeigt den Menüpunkt "Willkommensbildschirm" im Dashboard des Mandanten an.',
  },
  {
    key: 'sip_enabled',
    label: 'Telefonkontakte',
    description: 'Aktiviert Telefonkontakte und SIP-Anrufe am Kiosk. Zeigt den Menüpunkt im Dashboard des Mandanten an.',
  },
  {
    key: 'standby_settings_enabled',
    label: 'Standby-Bildschirm',
    description: 'Zeigt den Menüpunkt "Standby-Bildschirm" im Dashboard des Mandanten an.',
  },
  {
    key: 'theme_settings_enabled',
    label: 'Darstellung (Hell/Dunkel)',
    description: 'Erlaubt dem Mandant-Admin, das Erscheinungsbild der Check-in-Station (Hell-/Dunkelmodus, eigene Farben) selbst einzustellen.',
  },
  {
    key: 'sms_enabled',
    label: 'SMS-Versand',
    description: 'Aktiviert den SMS-Versand an Besucher und die SMS-Benachrichtigungsoption für Admins in den Einstellungen. Erfordert eine gültige SMS-Lizenz.',
  },
  {
    key: 'email_enabled',
    label: 'E-Mail-Versand',
    description: 'Aktiviert den Versand von Check-in-Bestätigungen und die E-Mail-Benachrichtigungsoption für Admins in den Einstellungen.',
  },
  {
    key: 'lpr_enabled',
    label: 'Kennzeichen-Scans',
    description: 'Zeigt den Menüpunkt „Kennzeichen-Scans" im Dashboard an (Übersicht aller erfassten Fahrzeugkennzeichen).',
  },
  {
    key: 'safety_rules_enabled',
    label: 'Sicherheitsregeln',
    description: 'Zeigt den Menüpunkt „Sicherheitsregeln" im Dashboard an.',
  },
  {
    key: 'documents_enabled',
    label: 'Dokumente',
    description: 'Zeigt den Menüpunkt „Dokumente" im Dashboard an.',
  },
  {
    key: 'daily_report_enabled',
    label: 'Tagesbericht',
    description: 'Zeigt den Menüpunkt „Tagesbericht" im Dashboard an.',
  },
  {
    key: 'translations_enabled',
    label: 'UI-Texte',
    description: 'Zeigt den Menüpunkt „UI-Texte" im Dashboard an (individuelle Texte für das Kiosk-Interface).',
  },
  {
    key: 'license_plates_enabled',
    label: 'Kennzeichen-Whitelist',
    description: 'Zeigt den Menüpunkt „Kennzeichen" im Dashboard an (Verwaltung erlaubter Fahrzeugkennzeichen für die Kennzeichenerkennung).',
  },
  {
    key: 'allowlist_enabled',
    label: 'Personenzulassung',
    description: 'Zeigt den Menüpunkt „Personenzulassung" im Dashboard an.',
  },
  {
    key: 'deliveries_enabled',
    label: 'Lieferavis',
    description: 'Zeigt den Menüpunkt „Lieferavis" im Dashboard an (angekündigte Lieferungen per CSV-Import). '
               + 'Am Terminal wird die eingegebene Referenznummer mit dieser Liste abgeglichen — in jedem Flow, '
               + 'in dem das im Flow Builder eingeschaltet ist. Ausgeschaltet findet keine Prüfung statt.',
  },
  {
    key: 'destination_free_abandoned',
    label: 'Zielort ohne Check-in automatisch freigeben',
    description: 'Gibt einen Zielort nach einer Stunde wieder frei, wenn der zugehörige Check-in nie '
               + 'abgeschlossen wurde. Nur aktivieren, wenn die Einfahrt tatsächlich den abgeschlossenen '
               + 'Check-in voraussetzt. Wird das Tor nach einem Anruf von Hand geöffnet, steht der Fahrer '
               + 'trotz unvollständigem Check-in am Zielort — die Freigabe würde dann einen zweiten LKW '
               + 'an dieselbe Stelle schicken. Deaktiviert wird stattdessen in der Übersicht gekennzeichnet.',
  },
  {
    key: 'destination_show_unfinished',
    label: 'Hinweis auf nicht abgeschlossene Check-ins',
    description: 'Kennzeichnet in Warteschlange und Tagesraster Zuweisungen, hinter denen kein '
               + 'abgeschlossener Check-in steht („ohne Check-in"). Standardmäßig aktiv: der Hinweis '
               + 'verhindert, dass ein zweiter LKW an eine belegte Rampe geschickt wird. Wo das Tor '
               + 'regelmäßig nach einem Anruf von Hand geöffnet wird, ist ein unvollständiger Check-in '
               + 'der Normalfall — dort kann der Hinweis abgeschaltet werden.',
  },
  {
    key: 'destinations_enabled',
    label: 'Zielorte',
    description: 'Zeigt den Menüpunkt „Zielorte" im Dashboard an (Rampen, Wartebereiche und Tore mit Belegungsanzeige). Unabhängig von den Informationsseiten: ein Mandant kann Informationsseiten nutzen, ohne eine Rampenübersicht zu benötigen.',
  },
  {
    key: 'barrier_control_enabled',
    label: 'Schranken im Dashboard steuern',
    description: 'Zeigt in der Disposition Schaltflächen zum Öffnen, Schließen und Stoppen der Schranken an. '
               + 'Gilt nur für Schranken von Stationen, die diesem Mandanten gehören, und nur für Schranken, '
               + 'die in den Stationen zusätzlich als „Im Dashboard steuerbar" markiert sind und eine Adresse '
               + 'über WireGuard haben. Alle angemeldeten Benutzer des Mandanten können sie bedienen.',
  },
  {
    key: 'kiosk_display_control_enabled',
    label: 'Terminal-Anzeige im Dashboard steuern',
    description: 'Zeigt in der Disposition für jedes Terminal dieses Mandanten die Schaltflächen '
               + '„Automatisch", „Standby", „Schwarz" und „Geöffnet" an — z. B. um den Bildschirm '
               + 'außerplanmäßig abzuschalten oder an einem geschlossenen Tag doch zu öffnen. Ändert nur '
               + 'die Anzeige am Terminal, nie Schranken oder Rampenalarme. Ein laufender Check-in wird '
               + 'nicht unterbrochen. Gilt für Stationen, die diesem Mandanten gehören; alle angemeldeten '
               + 'Benutzer des Mandanten können sie bedienen.',
  },
  {
    key: 'users_enabled',
    label: 'Benutzerverwaltung',
    description: 'Zeigt den Menüpunkt „Benutzerverwaltung" im Dashboard an. Ermöglicht Mandant-Admins, weitere Benutzerkonten anzulegen und zu verwalten.',
  },
]

const TenantFeatures = () => {
  const { apiFetch } = useAuth()

  const [tenants, setTenants]               = useState([])
  const [selectedTenant, setSelectedTenant] = useState('')
  const [features, setFeatures]             = useState(DEFAULT_FEATURES)
  const [loading, setLoading]               = useState(true)
  const [saving, setSaving]                 = useState(false)
  const [error, setError]                   = useState(null)
  const [success, setSuccess]               = useState(false)

  // Load tenant list
  useEffect(() => {
    apiFetch('/api/admin/tenants')
      .then(r => r.json())
      .then(data => {
        const list = Array.isArray(data) ? data : []
        setTenants(list)
        if (list.length > 0) setSelectedTenant(list[0].id)
        else setLoading(false)
      })
      .catch(() => {
        setError('Mandanten konnten nicht geladen werden')
        setLoading(false)
      })
  }, [])

  // Load features when selected tenant changes
  useEffect(() => {
    if (!selectedTenant) return
    setLoading(true)
    setError(null)
    apiFetch(`/api/admin/tenant-features/${selectedTenant}`)
      .then(r => r.json())
      .then(data => setFeatures({ ...DEFAULT_FEATURES, ...data.features }))
      .catch(() => setError('Features konnten nicht geladen werden'))
      .finally(() => setLoading(false))
  }, [selectedTenant])

  const toggle = (key) =>
    setFeatures(prev => ({ ...prev, [key]: !prev[key] }))

  const handleSave = async () => {
    setSaving(true)
    setError(null)
    setSuccess(false)
    try {
      const res  = await apiFetch(`/api/admin/tenant-features/${selectedTenant}`, {
        method: 'PUT',
        body:   JSON.stringify(features),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error)
      setSuccess(true)
      setTimeout(() => setSuccess(false), 3000)
    } catch (err) {
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Funktionen</h1>
          <p>Feature-Flags pro Mandant verwalten</p>
        </div>
      </div>

      {error   && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">✓ Gespeichert</div>}

      {/* Tenant selector */}
      {tenants.length === 0 && !loading ? (
        <div className="card">
          <p className="tf-empty">Noch keine Mandanten vorhanden.</p>
        </div>
      ) : (
        <>
          <div className="card tf-tenant-card">
            <div className="form-group tf-tenant-form-group">
              <label>Mandant</label>
              <select
                className="form-control"
                value={selectedTenant}
                onChange={e => setSelectedTenant(e.target.value)}
              >
                {tenants.map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="card">
            <div className="section-title">Feature-Flags</div>

            {loading ? (
              <div className="loading-state"><span className="spinner" /></div>
            ) : (
              <>
                {FEATURE_LABELS.map(({ key, label, description }) => (
                  <div key={key} className="tf-feature-row">
                    <div>
                      <div className="tf-feature-label">{label}</div>
                      <div className="tf-feature-desc">{description}</div>
                    </div>
                    <label className="toggle tf-feature-toggle">
                      <input
                        type="checkbox"
                        checked={!!features[key]}
                        onChange={() => toggle(key)}
                      />
                      <span className="toggle-slider" />
                    </label>
                  </div>
                ))}

                <div className="tf-feature-row">
                  <div>
                    <div className="tf-feature-label">Mehrfache Check-ins pro Tag (Kiosk)</div>
                    <div className="tf-feature-desc">
                      Steuert, ob dieselbe Person am Check-in-Terminal mehrmals am selben Tag
                      einchecken kann. Betrifft nur Check-ins am Terminal, nicht die Online-Voranmeldung.
                    </div>
                  </div>
                  <select
                    className="form-control tf-feature-select"
                    value={features.kiosk_checkin_policy || ''}
                    onChange={e => setFeatures(prev => ({
                      ...prev,
                      kiosk_checkin_policy: e.target.value || null,
                    }))}
                  >
                    {KIOSK_CHECKIN_POLICY_OPTIONS.map(({ value, label }) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>

                {/* The view applies to the visitor list whether or not destinations
                    are used, so unlike the slot width below it is always offered. */}
                <div className="tf-feature-row">
                  <div>
                    <div className="tf-feature-label">Ansicht der Disposition</div>
                    <div className="tf-feature-desc">
                      Standardansicht beim Öffnen der Seite. Das Tagesraster zeigt Zielorte
                      über Zeitfenster, die Warteschlange eine Liste der Fahrzeuge mit
                      Referenznummer, Spedition und Kontaktdaten. Jeder Benutzer kann
                      zusätzlich für sich umschalten.
                    </div>
                  </div>
                  <select
                    className="form-control tf-feature-select"
                    value={features.destination_view ?? 'board'}
                    onChange={e => setFeatures(prev => ({ ...prev, destination_view: e.target.value }))}
                  >
                    <option value="board">Tagesraster</option>
                    <option value="queue">Warteschlange</option>
                  </select>
                </div>

                {/* Only meaningful with the Zielorte feature on, so it is hidden
                    otherwise rather than offering a setting with nothing to affect. */}
                {features.destinations_enabled && (
                  <div className="tf-feature-row">
                    <div>
                      <div className="tf-feature-label">Rasterweite der Disposition</div>
                      <div className="tf-feature-desc">
                        Zeilenhöhe des Tagesrasters auf der Zielorte-Übersicht. 30 Minuten bei
                        schnellem Rampenwechsel, sonst 60.
                      </div>
                    </div>
                    <select
                      className="form-control tf-feature-select"
                      value={features.destination_slot_minutes ?? 60}
                      onChange={e => setFeatures(prev => ({
                        ...prev,
                        destination_slot_minutes: Number(e.target.value),
                      }))}
                    >
                      <option value={30}>30 Minuten</option>
                      <option value={60}>60 Minuten</option>
                    </select>
                  </div>
                )}

                <div className="tf-save-row">
                  <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                    {saving
                      ? <><span className="spinner spinner-sm" /> Speichern...</>
                      : 'Speichern'}
                  </button>
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  )
}

export default TenantFeatures
